/**
 * CancelChargeDialog - Diálogo de confirmação para cancelar uma cobrança
 *
 * Permite informar motivo (opcional). Se a cobrança estiver sincronizada
 * com um provider, a cobrança é cancelada primeiro no provider e só depois
 * localmente (atomicidade — se o provider rejeitar, o cancelamento aborta).
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-06-09 v1.0.0 - Criação do dialog
 * RELEASE: 2026-07-01 v1.1.0 - Aviso quando cobrança é PRORATA (mid-cycle):
 *          o cancelamento revoga automaticamente o add-on adicionado.
 */
import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, XCircle } from 'lucide-react';
import { InlineNotice } from '@/components/ui/inline-notice';
import { useCancelChargeManual } from '@/hooks/useCancelChargeManual';

interface CancelChargeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cobrancaId: string;
  providerChargeId?: string | null;
  contaCorrenteId?: string | null;
  providerSlug?: string | null;
  /** v1.1.0: cobrança gerada por adição mid-cycle de add-on (proration).
   *  Quando true, exibe aviso de que o add-on será revogado da assinatura. */
  isProrated?: boolean;
}

export function CancelChargeDialog({
  open,
  onOpenChange,
  cobrancaId,
  providerChargeId,
  contaCorrenteId,
  providerSlug,
  isProrated = false,
}: CancelChargeDialogProps) {
  const [reason, setReason] = useState('');
  const mutation = useCancelChargeManual();

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const isSynced = !!providerChargeId && !!contaCorrenteId;

  const handleConfirm = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await mutation.mutateAsync({
        cobranca_id: cobrancaId,
        reason: reason.trim() || null,
        provider_charge_id: isSynced ? providerChargeId : null,
        conta_corrente_id: isSynced ? contaCorrenteId : null,
      });
      onOpenChange(false);
    } catch {
      // toast tratado no hook
    }
  };

  const submitting = mutation.isPending;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            Cancelar cobrança?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Essa ação cancela a cobrança e todas as parcelas ainda não pagas.
                Parcelas já pagas serão mantidas.
              </p>
              {isSynced && (
                <InlineNotice variant="warning">
                  Cobrança sincronizada com <strong>{providerSlug || 'provider'}</strong>.
                  Será cancelada também no provider antes de cancelar localmente.
                </InlineNotice>
              )}
              {isProrated && (
                <InlineNotice variant="warning">
                  <strong>Atenção:</strong> esta cobrança foi gerada pela adição de um
                  add-on no meio do ciclo. Ao cancelar, o add-on correspondente será
                  <strong> removido automaticamente da assinatura</strong> e o tenant
                  perderá o direito de uso da quantidade adicionada.
                </InlineNotice>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="cancel-reason">Motivo (opcional)</Label>
          <Textarea
            id="cancel-reason"
            placeholder="Ex: cliente desistiu, cobrança duplicada…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Voltar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={submitting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Cancelando…
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 mr-1.5" />
                Confirmar cancelamento
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
